import { useState } from 'react';
import { personNodes, relations, backTimeline } from '@/mocks/gossip';
import SectionHead from './SectionHead';

const typeMeta: Record<string, { dot: string; label: string }> = {
  star: { dot: 'bg-primary-500', label: '艺人' },
  brand: { dot: 'bg-accent-500', label: '品牌' },
  media: { dot: 'bg-secondary-500', label: '媒体/节目' },
  company: { dot: 'bg-foreground-700', label: '公司' },
};

const kindCls: Record<string, string> = {
  contract: 'bg-primary-100 text-primary-700',
  history: 'bg-accent-100 text-accent-700',
  media: 'bg-secondary-100 text-secondary-700',
  brand: 'bg-accent-100/70 text-accent-800',
};

const timeColor: Record<string, string> = {
  signal: 'border-secondary-300 text-secondary-600',
  action: 'border-accent-300 text-accent-700',
  boom: 'border-primary-400 text-primary-700',
  cool: 'border-foreground-300 text-foreground-600',
};

const nameOf = (id: string) => personNodes.find((n) => n.id === id)?.name ?? id;

export default function NetworkMap() {
  const [tab, setTab] = useState<'relation' | 'timeline'>('relation');

  return (
    <section id="network" className="scroll-mt-28 py-12 md:py-16 bg-background-50">
      <div className="mx-auto max-w-[1320px] px-4 md:px-6">
        <SectionHead
          kicker="RELATION & BACKSTAGE"
          kickerZh="把关系链摊开"
          title="人物关系与幕后关联"
          desc="谁和谁同门、谁和谁闹掰、谁在幕后操盘。我们把散落在热搜里的人物、品牌、公司与节目连成一张关系网，再按时间轴复盘一次热点的完整生命周期。"
        />

        <div className="mb-6 inline-flex rounded-full border border-background-200 bg-background-100/70 p-1">
          {(
            [
              { key: 'relation', label: '人物关系', icon: 'ri-user-follow-line' },
              { key: 'timeline', label: '幕后时间轴', icon: 'ri-history-line' },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm whitespace-nowrap transition-colors ${
                tab === t.key ? 'bg-foreground-950 text-background-50 font-semibold' : 'text-foreground-600'
              }`}
            >
              <i className={`${t.icon} text-base`}></i>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'relation' ? (
          <div className="animate-fade-rise">
            {/* legend chips */}
            <div className="mb-5 flex flex-wrap gap-x-5 gap-y-2">
              {Object.entries(typeMeta).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-2 text-xs text-foreground-600 whitespace-nowrap">
                  <span className={`h-2.5 w-2.5 rounded-full ${v.dot}`}></span>
                  {v.label}
                </span>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {relations.map((r) => (
                <div
                  key={`${r.from}-${r.to}`}
                  className="flex flex-col gap-2 rounded-xl border border-background-200 bg-background-100/50 p-5 transition-colors hover:border-accent-300"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2 font-heading text-base font-bold text-foreground-950 whitespace-nowrap">
                      <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span>
                      {nameOf(r.from)}
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400"></i>
                    <span className="inline-flex items-center gap-2 font-heading text-base font-bold text-foreground-950 whitespace-nowrap">
                      {nameOf(r.to)}
                      <span className="h-2.5 w-2.5 rounded-full bg-accent-500"></span>
                    </span>
                  </div>
                  <span
                    className={`inline-flex w-max items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap ${kindCls[r.kind]}`}
                  >
                    <i className="ri-links-line"></i>
                    {r.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {personNodes.map((n) => (
                <span
                  key={n.id}
                  className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 whitespace-nowrap"
                >
                  <span className={`h-2 w-2 rounded-full ${typeMeta[n.type].dot}`}></span>
                  {n.name}
                  <span className="text-xs text-foreground-400">{n.role}</span>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="animate-fade-rise grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {backTimeline.map((t) => (
              <div
                key={t.title}
                className="flex gap-4 rounded-xl border border-background-200 bg-background-100/50 p-5"
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 font-label text-xs font-bold ${timeColor[t.type]}`}
                >
                  {t.time}
                </span>
                <div>
                  <h4 className="font-heading text-base font-bold text-foreground-950">{t.title}</h4>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}